<template>
  <teleport to="body">
    <div class="container-fluid">
      <div class="bb-popup-bg d-flex justify-content-center align-items-center">
        <div
          class="card shadow bb-card bb-popup-card mb-2 rounded bb-popup-table-card"
        >
          <div
            class="container-fluid p-4 bb-tooltip-wrapper bb-popup-table-body"
          >
            <div class="bb-hdg mb-3 text-center">Party Runes</div>

            <div
              v-if="errorMessage"
              class="alert alert-danger mb-3"
            >
              {{ errorMessage }}
            </div>

            <div
              v-if="isLoading"
              class="bb-loader bb-popup-table-loader"
            ></div>

            <PartyRunesTable
              v-model:searchTerm="searchTerm"
              :partyCombatants="partyCombatants"
              :npcCombatants="npcCombatants"
              :rows="rows"
              :emptyMessage="tableEmptyMessage"
              :searchEnabled="hasCharacters"
              @close="handleClose"
            />
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { axios } from '@/common/api.service.js'
import { mapGetters } from 'vuex'

import PartyRunesTable from '../components/PartyRunesTable.vue'

export default {
  name: 'PartyRunesView',
  components: {
    PartyRunesTable,
  },
  data() {
    return {
      allRows: [],
      searchTerm: '',
      isLoading: false,
      isLookupScheduled: false,
      errorMessage: '',
      partyCombatants: [],
      npcCombatants: [],
      rows: [],
      pendingLookupHandle: null,
      latestLookupId: 0,
    }
  },
  computed: {
    ...mapGetters('combat', {
      getCampaign: 'getCampaign',
    }),
    arenaSlug() {
      return (
        this.$route.params.slug ||
        this.$route.query.slug ||
        this.getCampaign()?.slug ||
        ''
      )
    },
    minimumLookupLength() {
      return 3
    },
    hasCharacters() {
      return this.partyCombatants.length > 0 || this.npcCombatants.length > 0
    },
    tableEmptyMessage() {
      if (!this.arenaSlug) {
        return 'No active combat campaign is loaded.'
      }

      if (!this.hasCharacters) {
        return 'No Alive Party Characters/NPCs'
      }

      const trimmedSearch = this.searchTerm.trim()
      if (!trimmedSearch) {
        return ''
      }

      if (trimmedSearch.length < this.minimumLookupLength) {
        return `Enter at least ${this.minimumLookupLength} characters before lookup runs.`
      }

      if (this.isLoading || this.isLookupScheduled) {
        return 'Loading matching runes...'
      }

      if (this.rows.length > 0) {
        return ''
      }

      return `No runes matched "${trimmedSearch}".`
    },
  },
  watch: {
    searchTerm: {
      handler(newValue) {
        this.scheduleLookup(newValue)
      },
    },
  },
  mounted() {
    this.fetchRuneMatrix('')
  },
  beforeUnmount() {
    this.clearPendingLookup()
  },
  methods: {
    clearPendingLookup() {
      if (this.pendingLookupHandle) {
        window.clearTimeout(this.pendingLookupHandle)
        this.pendingLookupHandle = null
      }

      this.isLookupScheduled = false
    },
    scheduleLookup(value) {
      this.clearPendingLookup()
      this.errorMessage = ''

      const trimmedValue = (value || '').trim()

      if (!trimmedValue || trimmedValue.length < this.minimumLookupLength) {
        // Show ALL runes
        this.rows = this.allRows
        this.isLoading = false
        return
      }

      this.isLookupScheduled = true

      this.pendingLookupHandle = window.setTimeout(() => {
        this.isLookupScheduled = false
        this.fetchRuneMatrix(trimmedValue)
      }, 450)
    },
    async fetchRuneMatrix(query) {
      if (!this.arenaSlug) {
        this.errorMessage = 'No active combat campaign is loaded.'
        this.rows = []
        return
      }

      const lookupId = ++this.latestLookupId
      this.isLoading = true

      try {
        const response = await axios.get(
          'api/v1/character-runes/party-viewer/',
          {
            params: {
              arena_slug: this.arenaSlug,
              q: query,
            },
          },
        )

        if (lookupId !== this.latestLookupId) {
          return
        }

        this.partyCombatants = response.data.party_combatants || []
        this.npcCombatants = response.data.npc_combatants || []
        this.rows = response.data.rows || []

        if (!query) {
          this.allRows = this.rows // store full list for later
        }
      } catch (error) {
        if (lookupId !== this.latestLookupId) {
          return
        }

        this.rows = []
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.arena_slug?.[0] ||
          'Unable to load combat runes.'
      } finally {
        if (lookupId === this.latestLookupId) {
          this.isLoading = false
        }
      }
    },
    handleClose() {
      if (this.$route.name === 'manageCombat') {
        const query = { ...this.$route.query }
        delete query.popup

        this.$router.replace({
          name: 'manageCombat',
          params: this.arenaSlug ? { slug: this.arenaSlug } : {},
          query,
        })
        return
      }

      if (this.arenaSlug) {
        this.$router.replace({
          name: 'manageCombat',
          params: { slug: this.arenaSlug },
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
  },
}
</script>

<style scoped>
.bb-popup-table-card {
  width: min(72rem, 95vw);
  height: min(80vh, 60rem);
  max-height: min(80vh, 60rem);
  position: relative;
  overflow: hidden;
}

.bb-popup-table-body {
  padding: 2rem 2rem 1rem !important;
}

.bb-popup-table-loader {
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .bb-popup-table-card {
    width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
    height: calc(100vh - 1rem);
  }

  .bb-popup-table-body {
    padding: 1.25rem 1.25rem 1rem !important;
  }
}
</style>

<style scoped>
.bb-popup-table-card {
  margin-left: auto;
  margin-right: 1.5rem;
}
</style>
